/**
 * PDF 保存兼容工具
 *
 * 背景：jsPDF 的 `save()` 依赖浏览器 `HTMLAnchorElement.prototype.download`
 * 属性触发下载。桌面浏览器支持；部分移动浏览器（iOS Safari 旧版、微信内置、
 * 部分国产 Android 浏览器）不支持该属性，导致点击后导航到 `blob:` 地址且
 * 无法打开（报告打不开）。
 *
 * 方案：检测 `download` 属性支持情况——
 * - 支持（桌面）：走 `pdf.save(filename)`，正常触发下载。
 * - 不支持（移动端）：用 `pdf.output('blob')` 生成 blob，`window.open`
 *   新标签打开 PDF 预览，用户通过浏览器自带 UI（iOS Safari 分享→存储到
 *   "文件"、Android Chrome 下载按钮等）保存。
 */

interface JsPdfCompat {
  save: (filename: string) => void;
  output: (type: 'blob') => Blob;
}

/**
 * 检测当前浏览器是否支持 `<a download>` 下载属性。
 */
function supportsDownloadAttribute(): boolean {
  try {
    return (
      typeof HTMLAnchorElement !== 'undefined' &&
      'download' in HTMLAnchorElement.prototype
    );
  } catch {
    return false;
  }
}

/**
 * 以兼容方式保存 PDF：桌面触发下载，移动端新标签打开预览。
 *
 * @param pdf   jsPDF 实例（或实现 save/output('blob') 的兼容对象）
 * @param filename  建议的文件名（仅桌面下载时使用）
 */
export function savePdfCompat(pdf: JsPdfCompat, filename: string): void {
  if (supportsDownloadAttribute()) {
    pdf.save(filename);
    return;
  }
  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url);
  // 延迟释放 blob URL，避免预览时失效
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
